import React from 'react';

export const MetricCardSkeleton: React.FC = () => (
  <div className="bg-white box-border content-stretch flex flex-col items-start relative rounded-[6px] shadow-[0px_3px_12px_0px_rgba(47,43,61,0.14)] size-full animate-pulse">
    <div className="box-border content-stretch flex gap-px items-center p-[24px] relative shrink-0 w-full">
      <div className="basis-0 content-stretch flex flex-col gap-[4px] grow items-start min-h-px min-w-px relative shrink-0">
        <div className="content-stretch flex gap-[16px] items-center relative shrink-0 w-full">
          <div className="basis-0 flex flex-col grow justify-center min-h-px min-w-px relative shrink-0">
            <div className="h-6 bg-gray-200 rounded w-32"></div>
          </div>
          <div className="content-stretch flex flex-col items-center relative shrink-0">
            <div className="bg-gray-200 box-border content-stretch flex items-center p-[8px] relative rounded-[6px] shrink-0 w-10 h-10"></div>
          </div>
        </div>
        <div className="flex flex-col justify-center min-w-full relative shrink-0">
          <div className="h-10 bg-gray-300 rounded w-20 my-2"></div>
        </div>
        <div className="content-stretch flex gap-[6px] items-center relative shrink-0">
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
      </div>
    </div>
  </div>
);

export const PatientQueueListSkeleton: React.FC = () => (
  <div className="bg-white box-border content-stretch flex flex-col items-start mb-8 relative rounded-[6px] shadow-[0px_3px_12px_0px_rgba(61,49,43,0.14)] size-full">
    {/* Header Section */}
    <div className="box-border content-stretch flex gap-[24px] items-start p-[24px] relative shrink-0 w-full">
      <div className="basis-0 content-stretch flex flex-col gap-[4px] grow items-start min-h-px min-w-px relative shrink-0 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-48 mb-2"></div>
        <div className="h-6 bg-gray-100 rounded w-64"></div>
      </div>
    </div>

    {/* Assignment Cards Skeleton */}
    <div className="box-border content-stretch flex flex-col items-center pb-0 pt-[24px] px-0 relative shrink-0 w-full">
      <div className="relative shrink-0 w-full">
        <div className="border-b border-[#e4e4e4] w-full"></div>
      </div>
      <div className="box-border content-stretch flex flex-col gap-[0px] items-stretch p-[24px] relative rounded-bl-[12px] rounded-br-[12px] shrink-0 w-full">
        {[1, 2, 3].map((index) => (
          <div
            key={index}
            className={`bg-white box-border content-stretch flex flex-col lg:grid lg:grid-cols-12 items-start lg:items-center gap-[24px] px-[20px] py-[24px] relative rounded-[6px] shrink-0 w-full ${index < 3 ? 'border-b border-[#e4e4e4]' : ''} animate-pulse`}
          >
            <div className="content-stretch flex flex-col gap-[4px] items-start relative shrink-0 w-full lg:col-span-4">
              <div className="h-8 bg-gray-200 rounded w-40 mb-2"></div>
              <div className="h-6 bg-gray-100 rounded w-56"></div>
            </div>
            <div className="w-full lg:col-span-2 flex justify-center items-center">
              <div className="h-10 bg-gray-100 rounded-lg w-32"></div>
            </div>
            <div className="w-full lg:col-span-1 text-center flex justify-center items-center">
              <div className="h-8 bg-gray-200 rounded w-12"></div>
            </div>
            <div className="w-full lg:col-span-3">
              <div className="h-16 bg-gray-100 rounded-lg border border-gray-200"></div>
            </div>
            <div className="w-full lg:col-span-2 flex justify-center items-center">
              <div className="h-8 bg-gray-100 rounded-lg w-28"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const DepartmentQueueSkeleton: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200">
    {/* Section Title */}
    <div className="p-6 border-b border-gray-200 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-64 mb-2"></div>
      <div className="h-4 bg-gray-100 rounded w-96"></div>
    </div>

    {/* Department Tabs Skeleton */}
    <div className="px-6 pt-4 border-b border-gray-200">
      <div className="flex space-x-6 animate-pulse">
        {[1, 2, 3].map((index) => (
          <div key={index} className="pb-4">
            <div className="h-6 bg-gray-200 rounded w-32"></div>
          </div>
        ))}
      </div>
    </div>

    {/* Department Content Skeleton */}
    <div className="animate-pulse">
      <div className="box-border content-stretch flex items-center justify-between px-[24px] py-[24px] relative w-full">
        <div className="basis-0 content-stretch flex gap-[12px] grow items-center min-h-px min-w-px relative shrink-0">
          <div className="bg-gray-200 box-border content-stretch flex items-center justify-center p-[6px] relative rounded-[6px] shrink-0 size-[55px]"></div>
          <div className="content-stretch flex flex-col gap-[3px] items-start relative shrink-0">
            <div className="h-8 bg-gray-200 rounded w-40 mb-2"></div>
            <div className="h-5 bg-gray-100 rounded w-64"></div>
          </div>
        </div>
      </div>

      {/* Room Cards Skeleton */}
      <div className="px-[24px] py-[24px]">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[24px]">
          {[1, 2, 3].map((index) => (
            <div key={index} className="bg-white box-border content-stretch flex flex-col gap-[40px] items-center justify-center p-[20px] relative rounded-[6px] shadow-[0px_3px_12px_0px_rgba(61,49,43,0.14)] w-full">
              <div className="content-stretch flex items-center relative shrink-0 w-full">
                <div className="basis-0 content-stretch flex flex-col gap-[4px] grow items-start min-h-px min-w-px relative shrink-0">
                  <div className="h-5 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-4 bg-gray-100 rounded w-40"></div>
                </div>
              </div>
              <div className="bg-gray-100 box-border content-stretch flex flex-col items-center p-[24px] relative rounded-[6px] shrink-0 w-full">
                <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-10 bg-gray-300 rounded w-40"></div>
              </div>
              <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
                <div className="h-5 bg-gray-200 rounded w-20 mb-2"></div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-8 bg-gray-100 rounded w-16 border border-gray-200"></div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);
